<template lang="pug">
Sheet(v-model:open="sheetOpen")
  SheetTrigger(as-child)
    Button.ml-1(
      type="button"
      variant="ghost"
      size="icon"
      class="inline-flex h-5 w-5 align-middle"
      title="编辑中继流量购买记录"
    )
      Icon(name="material-symbols:edit-outline-rounded" class="h-3.5 w-3.5")

  SheetContent(
    side="right"
    class="flex h-full w-[min(94vw,480px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[480px]"
  )
    SheetHeader(class="shrink-0 border-b px-5 py-4 pr-12 sm:px-7")
      SheetTitle 编辑中继流量购买记录
      SheetDescription 退款后人工修正购买记录和首充礼包状态；保存时会把两个字段的前后值写入 AbuseNotes。

    form(class="flex min-h-0 flex-1 flex-col" @submit.prevent="saveState")
      div(class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7")
        section.rounded-lg.border.p-4(class="bg-muted/30")
          p.text-xs.text-muted-foreground 当前服务
          p.mt-1.font-mono.text-sm service ID {{ service.id }} · plan ID {{ service.plan_id }}
          p.mt-2.break-all.font-mono.text-xs.text-muted-foreground(v-if="state") 周期 {{ state.period_key }}

        div.space-y-2
          Label(:for="fieldId('purchased-gb')") 本周期已买数量（GB）
          Input(
            :id="fieldId('purchased-gb')"
            v-model="purchasedGbInput"
            type="number"
            min="0"
            step="1"
            :disabled="loading || saving"
          )
          p.text-xs.text-muted-foreground(v-if="state && state.all_pre_limit > 0") 填写大于等于 0 的整数。达到 {{ state.period_purchase_limit_gb }}GB 后，本周期首充礼包视为已领取；仍可继续加购。
          p.text-xs.text-muted-foreground(v-else-if="state") 填写大于等于 0 的整数，累计购买量不设上限。

        div.space-y-2
          Label(:for="fieldId('all-pre')") all_pre（已买满的剩余周期数）
          Input(
            :id="fieldId('all-pre')"
            v-model="allPreInput"
            type="number"
            min="0"
            :max="state?.all_pre_limit"
            step="1"
            :disabled="loading || saving"
          )
          p.text-xs.text-muted-foreground(v-if="state && state.all_pre_limit > 0") 允许 0–{{ state.all_pre_limit }}，不能超过当前 buy_length。
          p.text-xs.text-muted-foreground(v-else-if="state") 当前套餐不支持 all_pre，只能设为 0。

        div.rounded-md.border.p-3.text-sm.text-muted-foreground(class="bg-muted/30")
          | 这里只修改购买记录，不会调整当前中继流量额度或退款金额。

        div.rounded-md.border.p-3.text-sm.text-destructive(
          v-if="loadError || formError"
          class="border-destructive/30 bg-destructive/5"
          role="alert"
        ) {{ loadError || formError }}

      SheetFooter(class="shrink-0 border-t px-5 py-4 sm:px-7")
        Button(type="button" variant="outline" :disabled="saving" @click="sheetOpen = false") 取消
        Button(type="submit" :disabled="loading || saving || !state || !hasChanges || Boolean(formError)")
          Icon(v-if="loading || saving" name="mdi:loading" class="mr-1.5 h-4 w-4 animate-spin")
          | {{ saving ? '保存中…' : loading ? '加载中…' : '保存购买记录' }}
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { toast } from '~/utils/sonner'

interface RelayDataAddonStateResponse {
  service_id: number
  period_key: string
  purchased_gb: number
  all_pre: number
  buy_length: number
  period_purchase_limit_gb: number
  all_pre_limit: number
}

interface RelayDataAddonStateFormCheck {
  purchasedGb: number | null
  allPre: number | null
  buyLength: number
  allPreLimit?: number
}

function parseNonNegativeIntegerInput(input: unknown): number | null {
  if (typeof input === 'string' && input.trim() === '') {
    return null
  }
  const value = Number(input)
  return Number.isSafeInteger(value) && value >= 0 ? value : null
}

function relayDataAddonStateFormError(input: RelayDataAddonStateFormCheck): string | null {
  if (input.purchasedGb === null) {
    return '本周期已买数量必须是大于等于 0 的整数'
  }
  if (input.allPre === null) {
    return 'all_pre 必须是大于等于 0 的整数'
  }
  const allPreLimit = input.allPreLimit ?? input.buyLength
  if (input.allPre > allPreLimit) {
    if (allPreLimit === 0) {
      return '当前套餐不支持 all_pre，只能设为 0'
    }
    return `all_pre 不能超过 buy_length ${input.buyLength}`
  }
  return null
}

function applyRelayDataAddonStateResponse(service: userServicesAttributes, response: RelayDataAddonStateResponse) {
  service.extra.data_addon_relay_period = {
    period_key: response.period_key,
    purchased_gb: response.purchased_gb,
  }
  if (response.all_pre === 0) {
    delete service.extra.all_pre
  } else {
    service.extra.all_pre = response.all_pre
  }
}

const props = defineProps<{
  service: userServicesAttributes
}>()
const emit = defineEmits<{
  saved: [state: RelayDataAddonStateResponse]
}>()

const sheetOpen = ref(false)
const loading = ref(false)
const saving = ref(false)
const loadError = ref('')
const state = ref<RelayDataAddonStateResponse | null>(null)
const purchasedGbInput = ref<string | number>('')
const allPreInput = ref<string | number>('')
const compId = useId()
const fieldId = (name: string) => `${compId}-${name}`

const parsedPurchasedGb = computed(() => parseNonNegativeIntegerInput(purchasedGbInput.value))
const parsedAllPre = computed(() => parseNonNegativeIntegerInput(allPreInput.value))
const formError = computed(() => {
  if (!state.value) {
    return null
  }
  return relayDataAddonStateFormError({
    purchasedGb: parsedPurchasedGb.value,
    allPre: parsedAllPre.value,
    buyLength: state.value.buy_length,
    allPreLimit: state.value.all_pre_limit,
  })
})
const hasChanges = computed(() => state.value != null && (parsedPurchasedGb.value !== state.value.purchased_gb || parsedAllPre.value !== state.value.all_pre))

async function loadState() {
  loading.value = true
  loadError.value = ''
  state.value = null
  purchasedGbInput.value = ''
  allPreInput.value = ''
  try {
    const data = await useAdmin.getRelayDataAddonState({
      serviceId: props.service.id,
    })
    if (Number(data.result.service_id) !== Number(props.service.id)) {
      throw new Error('接口返回了错误的服务')
    }
    state.value = data.result
    purchasedGbInput.value = String(data.result.purchased_gb)
    allPreInput.value = String(data.result.all_pre)
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : String(error)
  } finally {
    loading.value = false
  }
}

watch(sheetOpen, (open) => {
  if (open) {
    void loadState()
  }
})
watch(
  () => props.service.id,
  () => {
    if (sheetOpen.value) {
      void loadState()
    }
  },
)

async function saveState() {
  if (!state.value || !hasChanges.value || formError.value) {
    return
  }
  saving.value = true
  try {
    const data = await useAdmin.updateRelayDataAddonState({
      serviceId: props.service.id,
      purchasedGb: parsedPurchasedGb.value as number,
      allPre: parsedAllPre.value as number,
    })
    if (Number(data.result.service_id) !== Number(props.service.id)) {
      throw new Error('接口返回了错误的服务')
    }
    applyRelayDataAddonStateResponse(props.service, data.result)
    emit('saved', data.result)
    toast({
      title: '中继流量购买记录已更新',
      description: '前后值已记录到 AbuseNotes。',
    })
    sheetOpen.value = false
  } catch (error) {
    toast({
      title: '中继流量购买记录更新失败',
      description: error instanceof Error ? error.message : String(error),
      variant: 'destructive',
    })
  } finally {
    saving.value = false
  }
}
</script>
